import React, { useMemo } from 'react';
import { useOrganizationClientRecords } from '../../../../../../../hooks/api/useOrganizationClientRecords';
import { useIsDisabledByParent } from '../../../../../../v2BaseComponents/IsDisabledProvider';
import { Form } from '../../../../../../v2BaseComponents/Form';
import { AnimateLoadedContent } from '../../../../../../v2BaseComponents/Loader';
import AddClientRecordFields, {
    FormValue,
    formValueToCreateRequest,
    formValuesFromClientRecord,
    initialFormValue,
} from '../../../../../../shared/ClientRecord/ClientRecordFields';
import SideDrawerFormContainer, {
    ConfirmDirtyStateFormCloseContainer,
    SideDrawerDirtyFormExitValidationTracker,
} from '../../../../../../v2BaseComponents/Form/SideDrawerFormContainer';

export type EditClientRecordSideDrawerContentOwnProps = {
    onClose: (force: boolean) => void;
    clientRecordId: string;
};

export type EditClientRecordSideDrawerContentProps = EditClientRecordSideDrawerContentOwnProps;

const EditClientRecordSideDrawerContent: React.FC<EditClientRecordSideDrawerContentProps> = (props) => {
    const { updateClientRecord, isUpdatingClientRecord, clientRecord, isLoadingClientRecord } =
        useOrganizationClientRecords({
            onUpdateClientRecordSuccess: () => props.onClose(false),
            organizationClientRecordId: props.clientRecordId,
        });

    const isLoading = isLoadingClientRecord || isUpdatingClientRecord || !clientRecord;
    const initialValues = useMemo<FormValue>(
        () => (clientRecord?.clientDetails ? formValuesFromClientRecord(clientRecord) : initialFormValue),
        [clientRecord],
    );

    const isDisabled = useIsDisabledByParent(isLoading);
    return (
        <AnimateLoadedContent isLoading={isLoading}>
            {() => (
                <ConfirmDirtyStateFormCloseContainer>
                    <Form
                        fillContainer
                        initialValues={initialValues}
                        onSubmit={(values) => {
                            const request = formValueToCreateRequest(values);
                            updateClientRecord({
                                organizationClientRecordId: props.clientRecordId,
                                update: request,
                            });
                        }}
                        isDisabled={isDisabled}
                        forceStackFields
                    >
                        {({ isSubmitting, handleSubmit }) => {
                            return (
                                <SideDrawerFormContainer
                                    isDisabled={isDisabled || isSubmitting}
                                    onSubmit={handleSubmit}
                                    onCancel={() => props.onClose(true)}
                                >
                                    <SideDrawerDirtyFormExitValidationTracker />
                                    <AddClientRecordFields />
                                </SideDrawerFormContainer>
                            );
                        }}
                    </Form>
                </ConfirmDirtyStateFormCloseContainer>
            )}
        </AnimateLoadedContent>
    );
};

export default React.memo(EditClientRecordSideDrawerContent);
